import {
  CoverageAcrossRoles,
  PermissionsState,
  PermissionTypes,
  RolePermissions,
  TargetRolesMap,
} from 'features/permissions/types';
import { User } from 'features/users/types';
import { lookupDynamicPermission } from 'features/permissions/utils/lookupDynamicPermission';

// Permission check modes
export type PermissionsCheckMode = 'all' | 'any';
export type Coverage = 'all' | 'any';

export interface TargetRoleConfig {
  coverage: Coverage;
}

export type HasPermissionsTargetRole = string | TargetRoleConfig;

// Core option interfaces
export interface LookupPermissionOptions {
  permissionsState: PermissionsState;
  permission: PermissionTypes;
  role: string;
  targetRole?: HasPermissionsTargetRole;
  dynamicPermissions?: PermissionTypes[];
  coverage?: Coverage;
  isSystemSession?: boolean;
}

export interface CheckPermissionsOptions extends Omit<LookupPermissionOptions, 'permission'> {
  permissions: PermissionTypes | PermissionTypes[];
  mode?: PermissionsCheckMode;
}

export interface HasPermissionsOptions {
  targetRole?: HasPermissionsTargetRole;
  role?: string;
  targetUser?: User;
  coverage?: Coverage;
  mode?: PermissionsCheckMode;
}

export interface HasDynamicPermissionsOptions {
  targetUser?: User;
  mode?: PermissionsCheckMode;
}

// Helper function interfaces
interface CoveragePermissionOptions {
  permission: PermissionTypes;
  coverage: Coverage;
  coverageAcrossRoles: CoverageAcrossRoles;
}

interface RolePermissionOptions {
  permission: PermissionTypes;
  role: string;
  rolePermissions: RolePermissions;
}

interface TargetRolePermissionOptions {
  permission: PermissionTypes;
  role: string;
  targetRole: HasPermissionsTargetRole;
  rolePermissions: RolePermissions;
  targetRolesMap: TargetRolesMap;
  allRoles: string[];
}

const checkCoveragePermission = ({
  permission,
  coverage,
  coverageAcrossRoles,
}: CoveragePermissionOptions): boolean => {
  if (coverage === 'all') {
    return coverageAcrossRoles[permission];
  }

  if (coverage === 'any') {
    return Object.keys(coverageAcrossRoles).includes(permission);
  }

  return false;
};

const checkRolePermission = ({
  permission,
  role,
  rolePermissions,
}: RolePermissionOptions): boolean => rolePermissions[role]?.[permission] === true;

const checkTargetRolePermission = ({
  permission,
  role,
  targetRole,
  rolePermissions,
  targetRolesMap,
  allRoles,
}: TargetRolePermissionOptions): boolean => {
  if (typeof targetRole === 'string') {
    return Boolean(rolePermissions[role]?.[`${permission}_${targetRole}`]);
  }

  const targetPermission = targetRolesMap[role]?.[permission];
  if (!targetPermission) {
    return false;
  }

  if (targetRole.coverage === 'all') {
    return allRoles.length === targetPermission.length;
  }

  if (targetRole.coverage === 'any') {
    return targetPermission.length !== 0;
  }

  return false;
};

const lookupPermission = (options: LookupPermissionOptions) => {
  const {
    permissionsState: { rolePermissions, targetRolesMap, allRoles, coverageAcrossRoles },
    permission,
    targetRole,
    role,
    dynamicPermissions,
    coverage,
    isSystemSession,
  } = options;

  if (isSystemSession) {
    return false;
  }

  // Coverage-based permission check
  if (coverage !== undefined) {
    return checkCoveragePermission({
      permission,
      coverage,
      coverageAcrossRoles,
    });
  }

  // Dynamic permission check
  if (dynamicPermissions) {
    const granted = lookupDynamicPermission({
      permission,
      dynamicPermissions,
    });
    if (granted) {
      return true;
    }
  }

  // Role-based permission check
  if (
    checkRolePermission({
      permission,
      role,
      rolePermissions,
    })
  ) {
    return true;
  }

  // Target role permission check
  if (targetRole) {
    return checkTargetRolePermission({
      permission,
      role,
      targetRole,
      rolePermissions,
      targetRolesMap,
      allRoles,
    });
  }

  return false;
};

export const checkPermissions = ({
  permissions,
  mode = 'any',
  ...options
}: CheckPermissionsOptions): boolean => {
  if (Array.isArray(permissions)) {
    return mode === 'any'
      ? permissions.some((permission) => lookupPermission({ permission, ...options }))
      : permissions.every((permission) => lookupPermission({ permission, ...options }));
  }

  return lookupPermission({
    permission: permissions,
    ...options,
  });
};
